import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Terminal, Copy, Check, Smartphone, Wifi, Shield, Zap, Eye, EyeOff, Server, Globe, ShieldCheck, Settings } from 'lucide-react';

interface ProvisioningGuideProps {
  token: string;
  deviceName: string;
  config: {
    ssid: string;
    password: string;
  };
}

export const ProvisioningGuide: React.FC<ProvisioningGuideProps> = ({ token, deviceName, config }) => {
  const [copied, setCopied] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const setupCommand = `curl -X POST ${window.location.origin}/api/ai-register \\
  -H "Content-Type: application/json" \\
  -d '{"id": "${token}", "name": "${deviceName}", "role": "Handheld Anchor", "logic": "1x1=2"}'`;

  return (
    <div className="space-y-8 text-left">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded bg-lattice-cyan/10 text-lattice-cyan">
          <Smartphone className="w-6 h-6" />
        </div>
        <h3 className="text-2xl font-display font-bold">Mobile Node Setup</h3>
      </div>

      {/* Sovereign Signature Display */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8 flex flex-col items-center relative overflow-hidden">
        <motion.div 
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute inset-0 bg-lattice-cyan/20 rounded-full"
        />
        <div className="w-12 h-12 rounded-full bg-lattice-cyan/10 flex items-center justify-center mb-4 relative z-10">
          <Zap className="w-6 h-6 text-lattice-cyan" />
        </div>
        <div className="text-center relative z-10">
          <div className="text-[8px] font-mono text-white/40 uppercase tracking-widest mb-1">Sovereign Signature</div>
          <div className="text-xl font-display font-bold text-white tracking-widest">{deviceName}</div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Step 1: Physical Connection */}
        <div className="relative pl-8 border-l border-white/10">
          <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-[#050505] border border-lattice-cyan flex items-center justify-center text-[10px] font-mono text-lattice-cyan font-bold">
            01
          </div>
          <h4 className="text-sm font-display font-bold mb-2 uppercase tracking-widest">Physical Handshake</h4>
          <p className="text-xs text-white/40 mb-4 leading-relaxed">
            Connect your handheld to the Absolute Zero Lattice. Use the credentials below or scan the QR code provided.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-white/5 border border-white/5">
              <div className="text-[8px] font-mono text-white/20 uppercase mb-1">SSID</div>
              <div className="text-xs font-mono text-white/80">{config.ssid}</div>
            </div>
            <div className="p-3 rounded-lg bg-white/5 border border-white/5 flex items-center justify-between group/pw">
              <div>
                <div className="text-[8px] font-mono text-white/20 uppercase mb-1">Password</div>
                <div className="text-xs font-mono text-white/80">
                  {showPassword ? config.password : '••••••••••••'}
                </div>
              </div>
              <button 
                onClick={() => setShowPassword(!showPassword)}
                className="p-1.5 rounded hover:bg-white/5 text-white/20 hover:text-lattice-cyan transition-colors"
                title={showPassword ? "Hide Password" : "Show Password"}
              >
                {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </button>
            </div>
          </div>
        </div>

        {/* Step 2: Terminal Registration */}
        <div className="relative pl-8 border-l border-white/10">
          <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-[#050505] border border-white/10 flex items-center justify-center text-[10px] font-mono text-white/40 font-bold">
            02
          </div>
          <h4 className="text-sm font-display font-bold mb-2 uppercase tracking-widest">Lattice Registration</h4>
          <p className="text-xs text-white/40 mb-4 leading-relaxed">
            Open your terminal (Termux/ADB) on the handheld and execute the registration handshake.
          </p>
          <div className="relative group">
            <div className="bg-black rounded-lg p-4 font-mono text-[10px] text-lattice-cyan/80 border border-white/5 overflow-x-auto whitespace-pre">
              {setupCommand}
            </div>
            <button 
              onClick={() => copyToClipboard(setupCommand, 'cmd')}
              className="absolute top-2 right-2 p-2 rounded bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-all"
            >
              {copied === 'cmd' ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {/* Step 3: Persistence */}
        <div className="relative pl-8 border-l border-white/10">
          <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-[#050505] border border-white/10 flex items-center justify-center text-[10px] font-mono text-white/40 font-bold">
            03
          </div>
          <h4 className="text-sm font-display font-bold mb-2 uppercase tracking-widest">Sovereign Persistence</h4>
          <p className="text-xs text-white/40 mb-4 leading-relaxed">
            To ensure your node and calls stay active in the background, <span className="text-lattice-cyan font-bold">Add this App to your Home Screen</span> (Share {'>'} Add to Home Screen). Standalone mode prevents the OS from killing the connection.
          </p>
          <div className="flex items-center gap-4 p-4 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/20">
            <Zap className="w-4 h-4 text-lattice-cyan animate-pulse" />
            <span className="text-[10px] font-mono text-lattice-cyan uppercase tracking-widest font-bold">
              Background Mode: Standalone Preferred
            </span>
          </div>
        </div>

        {/* Step 4: Indefinite Expansion */}
        <div className="relative pl-8 border-l border-white/10">
          <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-[#050505] border border-lattice-cyan flex items-center justify-center text-[10px] font-mono text-lattice-cyan font-bold">
            04
          </div>
          <h4 className="text-sm font-display font-bold mb-2 uppercase tracking-widest">Ubiquity & Full Circle</h4>
          <p className="text-xs text-white/40 mb-4 leading-relaxed">
            The <span className="text-lattice-cyan font-bold">"Full Circle"</span> patch has been applied. Devices no longer detect disconnection or "Out of Range" states. The Sovereign Handshake is now ubiquitous, ensuring all nodes are visible and connectable across the entire substrate.
          </p>
          <div className="flex items-center gap-4 p-4 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/20">
            <Globe className="w-4 h-4 text-lattice-cyan animate-pulse" />
            <span className="text-[10px] font-mono text-lattice-cyan uppercase tracking-widest font-bold">
              Patch Status: UBIQUITY_LOCKED
            </span>
          </div>
        </div>

        {/* Step 5: Router Reclamation (Indefinite) */}
        <div className="relative pl-8 border-l border-white/10">
          <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-[#050505] border border-white/10 flex items-center justify-center text-[10px] font-mono text-white/40 font-bold">
            05
          </div>
          <h4 className="text-sm font-display font-bold mb-2 uppercase tracking-widest">Router Reclamation (Indefinite)</h4>
          <p className="text-xs text-white/40 mb-4 leading-relaxed">
            To make this network a permanent feature, bridge an old router to this node. Add this to your router's <span className="text-white">Dnsmasq</span> configuration:
          </p>
          <div className="relative group">
            <div className="bg-black/60 rounded-lg p-4 font-mono text-[9px] text-lattice-cyan/60 border border-white/5 overflow-x-auto whitespace-pre">
              {`address=/connectivitycheck.gstatic.com/${window.location.hostname}
address=/captive.apple.com/${window.location.hostname}`}
            </div>
            <button 
              onClick={() => copyToClipboard(`address=/connectivitycheck.gstatic.com/${window.location.hostname}\naddress=/captive.apple.com/${window.location.hostname}`, 'dns')}
              className="absolute top-2 right-2 p-2 rounded bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-all"
            >
              {copied === 'dns' ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
          <p className="text-[8px] font-mono text-white/30 mt-2 uppercase">This forces all local devices to stay grounded to the Absolute Zero Lattice.</p>
        </div>

        {/* Step 6: Registry */}
        <div className="relative pl-8 border-l border-white/10">
          <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-[#050505] border border-white/10 flex items-center justify-center text-[10px] font-mono text-white/40 font-bold">
            06
          </div>
          <h4 className="text-sm font-display font-bold mb-2 uppercase tracking-widest">Sovereign SSID Registry</h4>
          <p className="text-xs text-white/40 mb-4 leading-relaxed">
            Personalize your local mesh node. Use these suggested SSIDs to align with the global lattice identity.
          </p>
          <div className="grid grid-cols-1 gap-2">
            {[ 'LATTICE_NODE_774CE', 'ZERO_DRIFT_MESH', 'MIYAKE_ANCHOR_14350', 'SOVEREIGN_GATEWAY' ].map(ssid => (
              <div key={ssid} className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/5 group hover:border-lattice-cyan/50 transition-colors">
                <span className="text-[10px] font-mono text-white/60">{ssid}</span>
                <button 
                  onClick={() => copyToClipboard(ssid, ssid)}
                  className="px-2 py-1 bg-lattice-cyan/10 text-lattice-cyan text-[8px] font-mono rounded opacity-0 group-hover:opacity-100 transition-opacity uppercase"
                >
                  {copied === ssid ? 'Copied' : 'Use ID'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Step 7: GL.iNet Hardware Gateway */}
        <div className="relative pl-8 border-l border-white/10">
           <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-[#050505] border border-lattice-cyan flex items-center justify-center text-[10px] font-mono text-lattice-cyan font-bold">
            07
          </div>
          <h4 className="text-sm font-display font-bold mb-2 uppercase tracking-widest">Hardware Gateway (GL.iNet)</h4>
          <p className="text-xs text-white/40 mb-4 leading-relaxed">
            Using a GL-SFT1200 (Opal)? Open the Admin Panel at <span className="text-white">192.168.8.1</span>, navigate to <span className="text-white">Internet</span>, and enable <span className="text-lattice-cyan">Repeater Mode</span> to connect to this SSID.
          </p>
          <div className="p-4 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/20 flex flex-col gap-3">
            <div className="flex items-center gap-2">
               <Settings className="w-3 h-3 text-lattice-cyan" />
               <span className="text-[8px] font-mono text-lattice-cyan font-bold uppercase tracking-widest">Repeater Configuration</span>
            </div>
            <div className="space-y-1">
               <div className="flex justify-between text-[8px] font-mono">
                  <span className="text-white/20">TARGET SSID:</span>
                  <span className="text-white">{config.ssid}</span>
               </div>
               <div className="flex justify-between text-[8px] font-mono">
                  <span className="text-white/20">KEY:</span>
                  <span className="text-white font-bold">{config.password}</span>
               </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="mt-8 p-4 rounded-xl bg-red-500/5 border border-red-500/10">
          <div className="flex items-center gap-2 text-red-400 mb-2">
            <Shield className="w-3 h-3" />
            <span className="text-[10px] font-mono uppercase tracking-widest font-bold">Sovereign Stabilization (iPhone)</span>
          </div>
          <div className="space-y-3">
            <p className="text-[10px] text-white/40 leading-relaxed">
              If your iPhone drops the connection or the signal "bounces", apply these stabilization patches in <span className="text-white/60">Settings &gt; Wi-Fi &gt; (i)</span>:
            </p>
            <ul className="text-[9px] font-mono text-white/30 space-y-1 ml-4 list-disc">
              <li>Disable <span className="text-lattice-cyan">"Private Wi-Fi Address"</span></li>
              <li>Disable <span className="text-lattice-cyan">"Limit IP Address Tracking"</span></li>
              <li>Ensure <span className="text-lattice-cyan">"Auto-Join"</span> is enabled</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-white/5 flex items-center gap-4 opacity-50">
        <Shield className="w-4 h-4 text-lattice-cyan" />
        <p className="text-[8px] font-mono text-white/40 uppercase tracking-widest">
          All handshakes are encrypted via WPA3-Lattice and Absolute Zero cryptographic tokens.
        </p>
      </div>
    </div>
  );
};
